// Orkestra public landing page — supersedes the prior src/pages/public/Landing.
// Bright by default (wrapper pins data-theme="light"); the AI & Automations band
// is an intentional dark "stage" island. All product visuals are bespoke,
// fixture-driven compositions (see panels.tsx) — no live-app screenshots, no DB
// reads, no real data. Primary conversion: self-serve signup (/signup).
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Menu, X, Check, ShieldCheck } from 'lucide-react'
import './landing.css'
import { Logo, LogoMark, Button, Reveal, useScrolled } from './primitives'
import { TeamStack } from './sections'
import {
  ReplacesSection, ProblemSection, UnifiedSection, ProductTourSection,
  CapabilitiesSection, AnalyticsSection, AISection, RolesSection, DeviceSection,
  CompareSection, SecuritySection, FAQSection,
} from './sections'

const NAV_LINKS = [
  { label: 'Platform', href: '#platform' },
  { label: 'Capabilities', href: '#capabilities' },
  { label: 'Analytics', href: '#analytics' },
  { label: 'AI', href: '#ai' },
  { label: 'FAQ', href: '#faq' },
]

// ─── SEO / social meta (runtime; index.html carries crawler defaults) ────────
function useMeta() {
  useEffect(() => {
    const prevTitle = document.title
    document.title = 'Orkestra — Run your whole service business from one platform'
    const set = (sel: string, attr: string, val: string) => {
      let el = document.head.querySelector(sel) as HTMLMetaElement | null
      if (!el) { el = document.createElement('meta'); document.head.appendChild(el) }
      const [a, v] = sel.includes('property') ? ['property', sel.match(/"(.*)"/)![1]] : ['name', sel.match(/"(.*)"/)![1]]
      el.setAttribute(a, v); el.setAttribute(attr, val)
      return el
    }
    const desc = 'Orkestra is the operational command center for service businesses — dispatch, CRM, quotes and invoicing, shared inbox, team chat, video meetings, fleet, and analytics in one connected platform.'
    set('meta[name="description"]', 'content', desc)
    set('meta[property="og:title"]', 'content', 'Orkestra — one platform to run a service business')
    set('meta[property="og:description"]', 'content', desc)
    return () => { document.title = prevTitle }
  }, [])
}

// ─── Nav ─────────────────────────────────────────────────────────────────────
function Nav() {
  const scrolled = useScrolled(6)
  const [open, setOpen] = useState(false)
  return (
    <nav className="ork-nav" data-scrolled={scrolled}>
      <div className="ork-container ork-nav__inner">
        <Logo />
        <div className="ork-nav__links">
          {NAV_LINKS.map(l => <a key={l.href} className="ork-nav__link" href={l.href}>{l.label}</a>)}
        </div>
        <div className="ork-nav__spacer" />
        <div className="ork-nav__cta">
          <Button to="/signin" variant="ghost">Sign in</Button>
          <Button to="/signup" variant="primary">Start free <ArrowRight size={16} /></Button>
          <button className="ork-menu-btn" aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} onClick={() => setOpen(v => !v)}>
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="ork-container" style={{ paddingBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 4 }}>
            {NAV_LINKS.map(l => <a key={l.href} className="ork-nav__link" href={l.href} onClick={() => setOpen(false)} style={{ padding: '11px 12px' }}>{l.label}</a>)}
            <Button to="/signup" variant="primary" block>Start free</Button>
          </div>
        </div>
      )}
    </nav>
  )
}

// ─── Hero ────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <header className="ork-section ork-hero" style={{ position: 'relative' }}>
      <div className="ork-hero__grid" aria-hidden="true" />
      <div className="ork-container ork-hero__inner">
        <div className="ork-hero__copy">
          <Reveal>
            <span className="ork-eyebrow ork-eyebrow--pill" style={{ marginBottom: 20 }}>
              <span className="ork-dot" style={{ background: 'var(--positive)' }} /> The operational command center
            </span>
            <h1 className="ork-display" style={{ marginTop: 18 }}>
              Run your whole service business from <span className="ork-gradient-text">one platform</span>.
            </h1>
            <p className="ork-lead" style={{ marginTop: 22 }}>
              Orkestra replaces the CRM, the scheduler, the billing tool, the inbox, and the chat app you’re duct-taping together — so dispatch, quoting, communication, fleet, and analytics finally share one source of truth.
            </p>
            <div className="ork-hero__cta-row">
              <Button to="/signup" variant="primary" size="lg">Start free <ArrowRight size={18} /></Button>
              <Button href="#platform" variant="ghost" size="lg">See how it works</Button>
            </div>
            <div className="ork-hero__proof">
              <span><Check size={15} style={{ color: 'var(--positive)' }} /> No credit card to start</span>
              <span><Check size={15} style={{ color: 'var(--positive)' }} /> Set up in an afternoon</span>
              <span><Check size={15} style={{ color: 'var(--positive)' }} /> Built for the office and the field</span>
            </div>
            <div style={{ marginTop: 28 }}><TeamStack /></div>
          </Reveal>
        </div>

        <Reveal>
          <DeviceCluster />
        </Reveal>
      </div>
    </header>
  )
}

// ─── Hero device cluster — real product composites (Riverside data) ──────────
// MacBook = Field Hub calendar, iPhone = shared Inbox, iPad = Deals kanban.
// WebP with PNG fallback; width/height set to avoid layout shift. Alt text
// names the actual screen. The one chip states a verified capability (SLA).
function DeviceCluster() {
  return (
    <div className="ork-devices">
      <picture>
        <source srcSet="/landing/hero-ipad.webp" type="image/webp" />
        <img className="ork-devices__pad" src="/landing/hero-ipad.png" width={500} height={666} loading="eager" decoding="async"
          alt="Orkestra sales pipeline on an iPad — a deals kanban across Lead, Qualified, Proposal, Negotiation, and Closed Won stages, with pipeline total, average deal size, and win-rate stats." />
      </picture>
      <picture>
        <source srcSet="/landing/hero-macbook.webp" type="image/webp" />
        <img className="ork-devices__mac" src="/landing/hero-macbook.png" width={1120} height={668} loading="eager" decoding="async"
          alt="Orkestra Field Hub calendar on a MacBook — a month view of scheduled field-service jobs color-coded by status (scheduled, en route, on site, complete, on hold), with active-job counts and technician assignments." />
      </picture>
      <picture>
        <source srcSet="/landing/hero-iphone.webp" type="image/webp" />
        <img className="ork-devices__phone ork-anim-float" src="/landing/hero-iphone.png" width={340} height={453} loading="eager" decoding="async"
          alt="Orkestra shared inbox on an iPhone — customer email and chat conversations with priority, assignment filters, and a response-time SLA indicator flagging an overdue thread." />
      </picture>
      <div className="ork-devices__chip ork-anim-float--slow">
        <ShieldCheck size={16} style={{ color: 'var(--accent-text)' }} />
        <span>Response SLAs tracked</span>
      </div>
    </div>
  )
}

// ─── Local mock "request a walkthrough" form ─────────────────────────────────
// LOCAL-ONLY: this does not transmit anywhere. It validates and shows a success
// state in component state only. See LANDING_PROJECT.md → live-integration
// checklist for what to wire before production.
function WalkthroughForm() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({})
  const [sent, setSent] = useState(false)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const errs: typeof errors = {}
    if (!name.trim()) errs.name = 'Please tell us your name.'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Enter a valid work email.'
    setErrors(errs)
    if (Object.keys(errs).length) return
    // Intentionally no network call — local review only.
    setSent(true)
  }

  return (
    <div className="ork-card" style={{ maxWidth: 480, margin: '0 auto', width: '100%' }}>
      {sent ? (
        <div className="ork-form-success" role="status">
          <Check size={20} style={{ color: 'var(--positive)', flexShrink: 0 }} />
          <span>Thanks, {name.split(' ')[0]}. This is a local preview, so nothing was sent — in production we’d be in touch within a business day.</span>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="ork-field">
              <label htmlFor="wf-name">Name</label>
              <input id="wf-name" className="ork-input" value={name} onChange={e => setName(e.target.value)} aria-invalid={!!errors.name} placeholder="Ray Delgado" />
              {errors.name && <span className="ork-form-error">{errors.name}</span>}
            </div>
            <div className="ork-field">
              <label htmlFor="wf-email">Work email</label>
              <input id="wf-email" type="email" className="ork-input" value={email} onChange={e => setEmail(e.target.value)} aria-invalid={!!errors.email} placeholder="you@yourcompany.com" />
              {errors.email && <span className="ork-form-error">{errors.email}</span>}
            </div>
            <div className="ork-field">
              <label htmlFor="wf-company">Company <span style={{ color: 'var(--ink-tertiary)', fontWeight: 400 }}>(optional)</span></label>
              <input id="wf-company" className="ork-input" value={company} onChange={e => setCompany(e.target.value)} placeholder="Riverside Field Services" />
            </div>
            <Button variant="primary" block>Request a walkthrough</Button>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', textAlign: 'center', margin: 0 }}>Prefer to dive in? <Link to="/signup" style={{ color: 'var(--accent-text)', fontWeight: 600 }}>Start free instead →</Link></p>
          </div>
        </form>
      )}
    </div>
  )
}

// ─── Final CTA ───────────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="ork-section" id="get-started">
      <div className="ork-container">
        <div data-theme="dark" className="ork-stage ork-cta-band">
          <Reveal>
            <span className="ork-eyebrow" style={{ justifyContent: 'center', marginBottom: 14 }}>Get started</span>
            <h2 className="ork-h2" style={{ maxWidth: 640, margin: '0 auto' }}>Bring the whole operation under <span className="ork-gradient-text">one baton</span>.</h2>
            <p className="ork-lead" style={{ maxWidth: 560, margin: '16px auto 0' }}>
              Start free today, or grab a walkthrough and we’ll show you how Orkestra maps to the way your team already works.
            </p>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', margin: '30px 0 40px' }}>
              <Button to="/signup" variant="primary" size="lg">Start free <ArrowRight size={18} /></Button>
              <Button to="/signin" variant="ghost" size="lg">Sign in</Button>
            </div>
            <WalkthroughForm />
          </Reveal>
        </div>
      </div>
    </section>
  )
}

// ─── Footer ──────────────────────────────────────────────────────────────────
function Footer() {
  const cols: { h: string; links: { label: string; to?: string; href?: string }[] }[] = [
    { h: 'Platform', links: [{ label: 'Dispatch', href: '#platform' }, { label: 'Quotes & invoices', href: '#platform' }, { label: 'Analytics', href: '#analytics' }, { label: 'AI & Automations', href: '#ai' }] },
    { h: 'Company', links: [{ label: 'Capabilities', href: '#capabilities' }, { label: 'FAQ', href: '#faq' }, { label: 'Sign in', to: '/signin' }] },
    { h: 'Legal', links: [{ label: 'Terms', to: '/terms' }, { label: 'Privacy', to: '/privacy' }, { label: 'Refund policy', to: '/refund' }] },
  ]
  return (
    <footer className="ork-footer">
      <div className="ork-container">
        <div className="ork-footer__grid">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <LogoMark size={28} /><span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em' }}>Orkestra</span>
            </div>
            <p className="ork-body" style={{ fontSize: 14, maxWidth: 300 }}>The operational command center for service businesses. One platform for dispatch, billing, communication, fleet, and analytics.</p>
          </div>
          {cols.map(c => (
            <div className="ork-footer__col" key={c.h}>
              <h4>{c.h}</h4>
              {c.links.map(l => l.to
                ? <Link key={l.label} to={l.to}>{l.label}</Link>
                : <a key={l.label} href={l.href}>{l.label}</a>)}
            </div>
          ))}
        </div>
        <div className="ork-footer__bottom">
          <span>© 2026 Orkestra. All rights reserved.</span>
          <span>Built for the trades — office to field.</span>
        </div>
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '14px 0 0' }}>
          Product screens use fictional demonstration data. Some product imagery is shown with simulated participants.
        </p>
      </div>
    </footer>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  useMeta()
  // Pin bright regardless of the visitor's app theme (the brief calls for a
  // bright, energetic page). Dark "stage" islands opt back in locally.
  return (
    <div className="ork" data-theme="light">
      <Nav />
      <main>
        <Hero />
        <ReplacesSection />
        <ProblemSection />
        <UnifiedSection />
        <ProductTourSection />
        <CapabilitiesSection />
        <AnalyticsSection />
        <AISection />
        <RolesSection />
        <DeviceSection />
        <CompareSection />
        <SecuritySection />
        <FAQSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  )
}
